import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';

import { Observable } from '@shared/rx';
import {
  BackendApiType,
  EHeaderName,
  ICrowdinConfig,
  ObjectId,
} from '@shared/types';
import { get, includes, isEmpty, reduce } from 'lodash';
import { environment } from '@environments/environment';
import { lazyInject } from '@app/shared/decorators';
import { TokenSessionService } from '@app/shared/services/session/token-session.service';
import { generateObjectId } from '@app/shared/utils';
import { DEFAULT_LOCALE } from '@config/global-constants';
import { DmDataService, STATE_KEYS } from '@app/shared/services';
import { BACKEND_API_TYPE_TOKEN } from './http-context-tokens';
import { TranslationService } from '@app/shared/services/i18n';

/**
 * Notice: this interceptor works only with absolute URLs.
 *         It means that {@link UrlInterceptor} should be provided before.
 */
@Injectable()
export class SessionInterceptor implements HttpInterceptor {
  @lazyInject(TokenSessionService)
  public tokenSessionService: TokenSessionService;
  @lazyInject(TranslationService)
  public translationService: TranslationService;
  @lazyInject(DmDataService) public dmDataService: DmDataService;

  protected envBackendUrl: string | any = environment.xeaBackendURI;
  protected envXviBackendUrl: string | any = environment.xviBackendURI;
  protected envXprtUrl: string | any = environment.xprtBackendURI;
  protected envDMBackendUrl: string = environment.dmBackendURI;

  private xeaBaseUrls: string[];
  private SKIP_LOCALE_HEADER_URLS: string[] = [`/v1/token/verify`];

  public constructor() {
    this.init();
  }

  public intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (!this.isXenialUrl(req)) return next.handle(req);

    const token = this.tokenSessionService.token;
    const companyId = this.tokenSessionService.companyId;
    const siteId = this.tokenSessionService.siteId;
    const eventId = this.tokenSessionService.eventId;
    const employeeId = this.tokenSessionService.employeeId;

    const headers = reduce(
      [
        this.handleToken.bind(this, token),
        this.handleCompanyId.bind(this, req, companyId),
        this.handleSiteId.bind(this, req, [siteId]),
        this.handleRequestId.bind(this, req),
        this.handleEventId.bind(this, req, eventId),
        this.handleEmployeeId.bind(this, req, employeeId),
        this.handleLanguageParams.bind(this, req),
      ],
      (allHeaders, fn) => fn(allHeaders),
      req.headers,
    );

    const newReq = !isEmpty(get(req, 'headers.headers'))
      ? req
      : req.clone({ headers });

    return next.handle(newReq);
  }

  protected init(): void {
    this.xeaBaseUrls = [
      this.envBackendUrl,
      this.envXviBackendUrl,
      this.envXprtUrl,
      this.envDMBackendUrl,
    ];
  }

  /**
   * There are three possible state of {@link EHeaderName.Authorization} upon contact with the interceptor:
   */
  public handleToken(token, resHeaders: HttpHeaders): HttpHeaders {
    return resHeaders.set(EHeaderName.Authorization, `Bearer ${token}`);
  }

  public handleCompanyId(
    { headers }: HttpRequest<any>,
    companyId: ObjectId | null | undefined,
    resHeaders: HttpHeaders,
  ): HttpHeaders {
    const isExists = headers.has(EHeaderName.XCompanyId); /* avoid overriding */

    return companyId && !isExists
      ? resHeaders.set(EHeaderName.XCompanyId, companyId)
      : resHeaders;
  }

  public handleSiteId(
    { headers }: HttpRequest<any>,
    siteIds: ObjectId[] | null | undefined,
    resHeaders: HttpHeaders,
  ): HttpHeaders {
    const isExists = headers.has(EHeaderName.XSiteIds); /* avoid overriding */
    return !isEmpty(siteIds) && !isExists
      ? resHeaders.set(EHeaderName.XSiteIds, siteIds)
      : resHeaders;
  }

  private handleRequestId(
    { url }: HttpRequest<any>,
    resHeaders: HttpHeaders,
  ): HttpHeaders {
    return url &&
      (includes(url, BackendApiType.XVI) || includes(url, BackendApiType.XEA))
      ? resHeaders.set(EHeaderName.RequestId, generateObjectId())
      : resHeaders;
  }

  private handleLanguageParams(
    { url, context }: HttpRequest<any>,
    resHeaders: HttpHeaders,
  ): HttpHeaders {
    if (this.SKIP_LOCALE_HEADER_URLS.some((skipUrl) => url.includes(skipUrl))) {
      return resHeaders;
    }
    const { userLocale } = this.translationService?.getLocals() || {
      userLocale: DEFAULT_LOCALE,
    };
    const backendApiType = context.get(
      BACKEND_API_TYPE_TOKEN,
    ) as BackendApiType;

    const newHeader = resHeaders.set(EHeaderName.XLocales, userLocale);

    if (
      !(
        backendApiType &&
        [BackendApiType.XEA, BackendApiType.XVI].includes(backendApiType)
      )
    ) {
      return newHeader;
    }

    const crowdinConfig: ICrowdinConfig = get(
      this.dmDataService.companyPreferences,
      STATE_KEYS.XDM_CROWDIN_CUSTOM,
    );

    if (isEmpty(crowdinConfig?.crowdin_language_code)) {
      return newHeader;
    }

    return newHeader.set(
      EHeaderName.XLocalesPrefix,
      crowdinConfig?.crowdin_language_code,
    );
  }

  private handleEventId(
    { url }: HttpRequest<any>,
    eventId: ObjectId | null | undefined,
    resHeaders: HttpHeaders,
  ): HttpHeaders {
    return url && includes(url, BackendApiType.XEA) && eventId
      ? resHeaders.set(EHeaderName.XEventId, eventId)
      : resHeaders;
  }

  private handleEmployeeId(
    { url }: HttpRequest<any>,
    employeeId: ObjectId | null | undefined,
    resHeaders: HttpHeaders,
  ): HttpHeaders {
    return url && includes(url, BackendApiType.XEA) && employeeId
      ? resHeaders.set(EHeaderName.XEmployeeId, employeeId)
      : resHeaders;
  }

  /**
   * HttpClient can be used for requests to out of Xenial services. No need to send token there
   */
  private isXenialUrl({ url }: HttpRequest<any>): boolean {
    return this.xeaBaseUrls.some((sample) => url.indexOf(sample) === 0);
  }
}
